<script lang="ts">
  /**
   * ManageWorlds — overlay listing all saved worlds with Resume and Delete actions.
   */
  import { listWorlds, deleteWorld, loadWorld, getWorldState, getCharacter } from "$lib/api/invoke";
  import type { WorldInfo } from "$lib/api/invoke";
  import type { ResumeData } from "$lib/components/GameScreen.svelte";

  interface Props {
    onResume: (data: ResumeData) => void;
    onClose: () => void;
  }

  let { onResume, onClose }: Props = $props();

  let worlds = $state<WorldInfo[]>([]);
  let loading = $state(true);
  let error = $state("");
  let confirmDeleteId = $state<string | null>(null);

  async function refresh(): Promise<void> {
    loading = true;
    error = "";
    try {
      const list = await listWorlds();
      list.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
      worlds = list;
    } catch (err) {
      error = `Failed to load worlds: ${String(err)}`;
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    refresh();
  });

  async function handleResume(world: WorldInfo): Promise<void> {
    error = "";
    try {
      await loadWorld(world.id);
      const ws = await getWorldState();

      let charName = ws.character_name || "Adventurer";
      let charBackground = "";
      try {
        const charJson = await getCharacter();
        const char = JSON.parse(charJson);
        if (char.name) charName = char.name;
        if (char.background) charBackground = char.background;
      } catch {
        // non-fatal
      }

      onResume({
        characterName: charName,
        characterBackground: charBackground,
        turnNumber: ws.turn || 0,
        currentLocation: ws.location || "Unknown location",
        recentActions: ws.recent_actions || [],
        lastNarrative: ws.last_narrative || "",
      });
    } catch (err) {
      error = `Failed to resume world: ${String(err)}`;
    }
  }

  async function handleDelete(worldId: string): Promise<void> {
    error = "";
    try {
      await deleteWorld(worldId);
      confirmDeleteId = null;
      await refresh();
    } catch (err) {
      error = `Failed to delete world: ${String(err)}`;
    }
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      e.preventDefault();
      if (confirmDeleteId) {
        confirmDeleteId = null;
      } else {
        onClose();
      }
    }
  }

  function formatDate(iso: string): string {
    try {
      const d = new Date(iso);
      return (
        d.toLocaleDateString() +
        " " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    } catch {
      return iso;
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="manage-backdrop" onclick={onClose}>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="manage-panel" onclick={(e) => e.stopPropagation()}>
    <h2 class="manage-title">== Manage Worlds ==</h2>

    {#if loading}
      <p class="manage-status">Loading worlds...</p>
    {:else if error}
      <p class="manage-error">{error}</p>
    {:else if worlds.length === 0}
      <p class="manage-status">No saved worlds found.</p>
    {:else}
      <div class="manage-list">
        {#each worlds as world (world.id)}
          <div class="manage-world">
            <div class="manage-world__info">
              <span class="manage-world__name">{world.name}</span>
              <span class="manage-world__date">{formatDate(world.updated_at)}</span>
            </div>
            <div class="manage-world__actions">
              <button class="manage-btn manage-btn--resume" onclick={() => handleResume(world)}
                >Resume</button
              >
              {#if confirmDeleteId === world.id}
                <button
                  class="manage-btn manage-btn--confirm"
                  onclick={() => handleDelete(world.id)}>Confirm?</button
                >
                <button
                  class="manage-btn manage-btn--cancel"
                  onclick={() => {
                    confirmDeleteId = null;
                  }}>Cancel</button
                >
              {:else}
                <button
                  class="manage-btn manage-btn--delete"
                  onclick={() => {
                    confirmDeleteId = world.id;
                  }}>Delete</button
                >
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <button class="manage-close" onclick={onClose}>[ESC to close]</button>
  </div>
</div>

<style>
  .manage-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .manage-panel {
    background: var(--bg);
    border: 1px solid var(--fg-dim);
    padding: 1.5rem 2rem;
    max-width: 90vw;
    min-width: 30rem;
    max-height: 85vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  .manage-title {
    color: var(--fg-bright);
    font-size: 1rem;
    font-weight: bold;
    margin: 0;
  }

  .manage-status {
    color: var(--fg-dim);
    font-size: 0.85rem;
  }

  .manage-error {
    color: #ff4444;
    font-size: 0.85rem;
  }

  .manage-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .manage-world {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--fg-dim);
    gap: 1rem;
  }

  .manage-world__info {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .manage-world__name {
    color: var(--fg);
    font-size: 0.9rem;
  }

  .manage-world__date {
    color: var(--fg-dim);
    font-size: 0.7rem;
  }

  .manage-world__actions {
    display: flex;
    gap: 0.5rem;
  }

  .manage-btn {
    background: none;
    border: 1px solid var(--fg-dim);
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.8rem;
    cursor: pointer;
    padding: 0.25rem 0.75rem;
  }

  .manage-btn:hover {
    color: var(--fg-bright);
    border-color: var(--fg);
  }

  .manage-btn--resume:hover {
    color: #33ff33;
    border-color: #33ff33;
  }

  .manage-btn--delete:hover {
    color: #ff4444;
    border-color: #ff4444;
  }

  .manage-btn--confirm {
    color: #ff4444;
    border-color: #ff4444;
  }

  .manage-btn--cancel {
    color: var(--fg-dim);
  }

  .manage-close {
    background: none;
    border: none;
    color: var(--fg-dim);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0.5rem 1rem;
    margin-top: 0.25rem;
  }

  .manage-close:hover {
    color: var(--fg);
  }
</style>
